import * as React from "react";
import { Box, Paper, Typography } from "@mui/material";
import { RunningTotalsProps } from "../../../../types";

export interface MobileKpiViewProps {
  runningTotals: RunningTotalsProps;
  formatCurrency: (value: number) => string;
  exitStrategy: number;
  totalCost: number;
  profitUSD: number;
  profitMargin: number;
  varianceToExitStrategy: number;
  /** When true, renders "N/A" for NaN/Infinity variance (create pages). */
  varianceNAGuard?: boolean;
  /** Spec-only KPI fields */
  paType?: "spec" | "sold";
  bpPrice?: number;
  bpProfit?: number;
  bpPercent?: number;
  blPrice?: number;
  blProfit?: number;
  blPercent?: number;
  exitPrice?: number;
  exitProfit?: number;
  exitPercent?: number;
}

interface GroupedKpiCardProps {
  label: string;
  rows: { label: string; value: string }[];
}

function GroupedKpiCard({ label, rows }: GroupedKpiCardProps) {
  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 1.5, py: 0.75, backgroundColor: "rgba(0,0,0,0.03)", borderBottom: "0.5px solid", borderColor: "divider" }}>
        <Typography variant="body2" fontWeight={600} sx={{ letterSpacing: "0.08em", textTransform: "uppercase", color: "text.secondary" }}>
          {label}
        </Typography>
        {rows.find(r => r.label === "%") && (
          <Typography variant="body2" fontWeight={600} sx={{ color: "#1a7a3f" }}>
            {rows.find(r => r.label === "%")!.value}
          </Typography>
        )}
      </Box>
      {rows.filter(r => r.label !== "%").map((row, i, arr) => (
        <Box key={row.label} sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", px: 1.5, py: 0.75, borderTop: i === 0 ? "none" : "0.5px solid", borderColor: "divider" }}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {row.label}
          </Typography>
          <Typography variant="body2" fontWeight={600} sx={{ letterSpacing: "-0.01em", color: "text.primary" }}>
            {row.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

interface KpiCardProps {
  label: string;
  value: string;
  color: string;
}

function KpiCard({ label, value, color }: KpiCardProps) {
  const isTotal = label === "Total Cost";
  return (
    <Box sx={{ px: 2, py: isTotal ? 1.5 : 2, borderTop: "0.5px solid", borderColor: "divider" }}>
      <Typography variant="caption" sx={{ letterSpacing: "0.09em", textTransform: "uppercase", color: "text.secondary", display: "block", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography
        fontWeight={600}
        sx={{ letterSpacing: "-0.02em", lineHeight: 1.1, color: isTotal ? "text.secondary" : color, fontSize: isTotal ? "1.1rem" : "1.4rem" }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/**
 * Full-screen KPI view (the "KPI / Stats" bottom-nav tab on mobile).
 */
export function MobileKpiView({
  runningTotals,
  formatCurrency,
  exitStrategy,
  totalCost,
  profitUSD,
  profitMargin,
  varianceToExitStrategy,
  varianceNAGuard = false,
  paType,
  bpPrice,
  bpProfit,
  bpPercent,
  blPrice,
  blProfit,
  blPercent,
  exitPrice,
  exitProfit,
  exitPercent,
}: MobileKpiViewProps) {
  const varianceDisplay = varianceNAGuard
    ? isNaN(varianceToExitStrategy) || !isFinite(varianceToExitStrategy)
      ? "N/A"
      : `${varianceToExitStrategy.toFixed(2)}%`
    : `${varianceToExitStrategy.toFixed(2) || 0}%`;

  const marginDisplay =
    runningTotals.contract === 0
      ? "N/A"
      : (profitMargin.toFixed(2) || 0) + "%";

  const varianceColor = exitStrategy <= profitMargin ? "#25D72C" : "red";

  const bpRows = [
    { label: "Price", value: bpPrice !== undefined ? formatCurrency(bpPrice) : "N/A" },
    { label: "Profit", value: bpProfit !== undefined ? formatCurrency(bpProfit) : "N/A" },
    { label: "%", value: bpPercent !== undefined ? `${bpPercent.toFixed(2)}%` : "N/A" },
  ];

  const blRows = [
    { label: "Price", value: blPrice !== undefined ? formatCurrency(blPrice) : "N/A" },
    { label: "Profit", value: blProfit !== undefined ? formatCurrency(blProfit) : "N/A" },
    { label: "%", value: blPercent !== undefined ? `${blPercent.toFixed(2)}%` : "N/A" },
  ];

  const exitRows = [
    { label: "Price", value: exitPrice !== undefined ? formatCurrency(exitPrice) : "N/A" },
    { label: "Profit", value: exitProfit !== undefined ? formatCurrency(exitProfit) : "N/A" },
    { label: "%", value: exitPercent !== undefined ? `${exitPercent.toFixed(2)}%` : "N/A" },
  ];


  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden", m: 2 }}>
      {/* Hero */}
      <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: "0.5px solid", borderColor: "divider" }}>
        <Typography variant="caption" sx={{ letterSpacing: "0.09em", textTransform: "uppercase", color: "text.secondary", display: "block", mb: 0.5 }}>
          {paType === "spec" ? "Full Retail" : "Contract Price"}
        </Typography>
        <Typography fontWeight={600} sx={{ fontSize: "1.6rem", letterSpacing: "-0.03em", lineHeight: 1, color: "text.primary" }}>
          {formatCurrency(runningTotals.contract)}
        </Typography>
      </Box>

      {paType !== "spec" && (
        <Box sx={{ p: 1.25, display: "flex", flexDirection: "column", gap: 1 }}>
          {[
            { label: "Profit USD", value: formatCurrency(profitUSD), color: "#25D72C" },
            { label: "Profit Margin", value: marginDisplay, color: "#25D72C" },
            { label: "Variance to Exit Strategy", value: varianceDisplay, color: varianceColor },
          ].map(({ label, value, color }) => (
            <Box key={label} sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", px: 1.5, py: 0.75 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", letterSpacing: "0.08em", textTransform: "capitalize" }}>
                  {label}
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color, letterSpacing: "-0.01em" }}>
                  {value}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {paType === "spec" && (
        <Box sx={{ p: 1.25, display: "flex", flexDirection: "column", gap: 1 }}>
          <GroupedKpiCard label="Exit" rows={exitRows} />
          <GroupedKpiCard label="BP" rows={bpRows} />
          <GroupedKpiCard label="BL" rows={blRows} />
        </Box>
      )}

      <KpiCard label="Total Cost" value={formatCurrency(totalCost)} color="#1a7a3f" />
    </Box>
  );
}
